import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getCachedUrl, setCachedUrl } from "@/lib/imageUrlCache";

const SIGN_TTL_SECONDS = 3600;
// Refresh a bit before the signed URL actually expires so we never hand out
// a stale link that would 403 in <img>.
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

export interface ImageTransform {
  width?: number;
  height?: number;
  quality?: number;
  resize?: "cover" | "contain" | "fill";
}

/**
 * Resolves a storage path to a signed URL. If `path` is already a full URL, returns it.
 *
 * Tier-1 cache: signed URLs are cached in-memory AND persisted to IndexedDB
 * (see src/lib/imageUrlCache.ts) so repeat opens within ~55 min serve the URL
 * synchronously without re-calling Supabase. Pass `transform` for resized
 * thumbnails — the transform params are folded into the cache key so each
 * variant is cached independently.
 */
export function useSignedImage(
  bucket: "user-photos" | "product-images" | "tryon-results" | "featured-images",
  path: string | null | undefined,
  transform?: ImageTransform,
): string | null {
  const transformKey = transform
    ? `${transform.width ?? ""}x${transform.height ?? ""}q${transform.quality ?? ""}r${transform.resize ?? ""}`
    : "";

  // Compute initial state synchronously from the in-memory cache so the
  // first render already has the URL when the user re-opens a screen.
  const key = path ? `${bucket}|${path}|${transformKey}` : "";
  const initial = (() => {
    if (!path) return null;
    if (/^https?:\/\//.test(path) || path.startsWith("data:")) return path;
    return getCachedUrl(key)?.url ?? null;
  })();

  const [url, setUrl] = useState<string | null>(initial);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      return;
    }
    if (/^https?:\/\//.test(path) || path.startsWith("data:")) {
      setUrl(path);
      return;
    }
    const cached = getCachedUrl(key);
    if (cached) {
      setUrl(cached.url);
      return;
    }
    let active = true;
    const opts = transform
      ? { transform: { width: transform.width, height: transform.height, quality: transform.quality, resize: transform.resize } }
      : undefined;
    supabase.storage
      .from(bucket)
      .createSignedUrl(path, SIGN_TTL_SECONDS, opts)
      .then(({ data, error }) => {
        if (!active) return;
        if (error || !data) return;
        const expiresAt = Date.now() + SIGN_TTL_SECONDS * 1000 - REFRESH_BUFFER_MS;
        setCachedUrl(key, data.signedUrl, expiresAt);
        setUrl(data.signedUrl);
      });
    return () => {
      active = false;
    };
  }, [bucket, path, transformKey, key, transform]);

  return url;
}
